import { observer } from 'mobx-react';
import { useCallback, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';

// Constants
import { StripeError } from 'constants/Errors';

// Hooks
import useQuery from 'hooks/use-query';
import { useStores } from 'hooks/use-stores';

// Components / Pages
import ReaderDisconnectedModal from 'components/kiosk/ReaderDisconnectedModal';
import ReaderPairingModal from 'components/kiosk/ReaderPairingModal';
import LoadingScreen from 'components/LoadingScreen';
import { notification } from 'top-component-library';

// Utils
import { lazyWithRetry } from 'utils/DynamicContent';
import { uncensorTwilio } from 'utils/Host';
import { eventBus } from 'utils/Utility';

// Tracking
import CartItemAddModalContainer from 'components/cart-item-editor/CartItemAddModalContainer';
import CartItemEditModalContainer from 'components/cart-item-editor/CartItemEditModalContainer';
import { trackLocationCodeNotFound } from 'integrations/segment/tracking-events/menuPage';

import { LockType } from 'stores/LockStore';
import { TabStatus } from '../api/types';

// Routes
const MenuPage = lazyWithRetry(async () => await import('pages/MenuPage'));
const ClaimPOSTab = lazyWithRetry(async () => await import('pages/ClaimPOSTab'));
const CheckoutRoute = lazyWithRetry(async () => await import('routes/CheckoutRoute'));

function LocationRoute() {
  const location = useLocation();
  const { locationCode } = useParams();
  const history = useHistory();
  const {
    featuresStore,
    menuDataStore,
    locationStore,
    tabStore,
    lockStore,
    uiState,
    checkoutStore,
    stripeTerminalStore,
  } = useStores();

  const { showClaimPOSInitiatedTabFlow } = featuresStore;
  const { isPollingMenuItems } = menuDataStore;
  const { selectedCart } = checkoutStore;
  const { sharedCartReference } = selectedCart ?? {};
  const {
    loaded,
    id,
    locationShortId,
    kiosk_enabled,
    show_screen_saver,
    kioskScreenSaverTimeout,
    debounceRedirectToMenuPage,
    cancelRedirectToMenuPage,
  } = locationStore;
  const { hasLock } = lockStore;
  const { performingCheckout } = stripeTerminalStore;

  const query = useQuery();
  const menuId = uncensorTwilio(query.get('menu'));

  useEffect(() => {
    if (uiState.history) {
      uiState.setHistory(history);
    }
  }, [uiState, history]);

  useEffect(() => {
    if (location.state?.joinedTab) {
      notification.success({ message: 'Tab joined successfully!' });
    }
  }, [location]);

  /** Sets the fulfillment method for the cart on page load. */
  useEffect(() => {
    if (loaded) {
      checkoutStore.determineInitialFulfillmentMethodOnPageLoad();
    }
  }, [loaded, checkoutStore]);

  useEffect(() => {
    (async () => {
      const tabIdFromUrl = uncensorTwilio(query.get('tabId'));
      const claimingTab = query.get('claimTab');

      if (tabIdFromUrl && claimingTab) {
        await tabStore.setActiveConsumerDataWithTabId(tabIdFromUrl);
      }
    })();
  }, [tabStore, loaded]);

  useEffect(() => {
    (async () => {
      try {
        await locationStore.fetchLocationData(locationCode);
      } catch (err) {
        if (err.status === 404) {
          trackLocationCodeNotFound({
            locationShortId: locationCode,
          });
        }

        const shouldRedirect = !lockStore.hasLock(LockType.RedirectPage);

        if (err instanceof StripeError && shouldRedirect) {
          history.push('/error', { error: err.message });
        } else if (shouldRedirect) {
          history.push('/', { error: err.message });
        }
      }
    })();
  }, [locationCode, history, locationStore]);

  const redirectToKioskScreenSaver = useCallback(() => {
    debounceRedirectToMenuPage(kioskScreenSaverTimeout);
  }, [kioskScreenSaverTimeout, debounceRedirectToMenuPage]);

  useEffect(() => {
    if (kiosk_enabled && !show_screen_saver && !performingCheckout) {
      redirectToKioskScreenSaver();
      eventBus.on('userInteraction', redirectToKioskScreenSaver);
    }

    return () => {
      eventBus.remove('userInteraction', redirectToKioskScreenSaver);
      cancelRedirectToMenuPage();
    };
  }, [kiosk_enabled, show_screen_saver, performingCheckout, redirectToKioskScreenSaver, cancelRedirectToMenuPage]);

  const menuLoadLocked = hasLock(LockType.MenuLoad);

  useEffect(() => {
    (async function () {
      if (loaded && !show_screen_saver && locationShortId === locationCode.toLowerCase() && !menuLoadLocked) {
        try {
          // NOTE: You must poll for available menu items before loading all menus because you need to know which items
          // are available on menu load so you can render the items based on the menu item availability rules.
          if (!isPollingMenuItems) {
            await menuDataStore.pollMenuItems(id); // Start polling for menu item changes on page load
          } else {
            // NOTE: If the menu data is already loaded then fetchMenuData will exit early
            await menuDataStore.fetchMenuData(menuId);
          }
        } catch (error) {
          console.error(error);
          history.push(`/`, {
            error: `Unable to retrieve menu data for the given table/location.`,
          });
        }
      }
    })();
  }, [
    history,
    loaded,
    menuLoadLocked,
    id,
    locationShortId,
    locationCode,
    menuDataStore,
    isPollingMenuItems,
    menuId,
    show_screen_saver,
  ]);

  useEffect(() => menuDataStore.stopPollingFulfillableMenuItems, [menuDataStore]);

  /**
   * Configures polling for cart updates while the user is on a menu or checkout page
   */
  useEffect(() => {
    (async function () {
      if (loaded && locationCode === locationShortId && sharedCartReference?.loaded) {
        try {
          if (!sharedCartReference.isPolling && sharedCartReference?.location_id === id) {
            await sharedCartReference.pollForUpdates();
          }
        } catch (error) {
          console.error(error);
          notification.error({ message: error.message });
        }
      }
    })();

    return () => {
      sharedCartReference?.stopPolling();
    };
  }, [loaded, id, locationCode, locationShortId, sharedCartReference]);

  useEffect(() => {
    const error = stripeTerminalStore.androidInitializationError ?? stripeTerminalStore.connectionError;
    if (error) {
      notification.error({ message: error?.message });
    }
  }, [stripeTerminalStore.connectionError, stripeTerminalStore.androidInitializationError]);

  useEffect(() => {
    // If menu data is loaded and we don't need to show the claim pos flow,
    // defer to the consumer clicking on the "claim tab" overlay to jump back into that flow.
    // This will stop the stores from forcing them into the flow as soon as pos tabs are available.
    if (loaded && !showClaimPOSInitiatedTabFlow) {
      tabStore.setShowUnclaimedPosOnlyTabs(false);
    }
  }, [loaded, showClaimPOSInitiatedTabFlow]);

  useEffect(() => {
    if (tabStore.activeConsumerTab && tabStore.activeConsumerTab.status === TabStatus.PendingCheckout) {
      history.push(`/${locationCode}/checkout#payment`);
    }
  }, []);

  if (!loaded) {
    return <LoadingScreen />;
  }

  if (showClaimPOSInitiatedTabFlow) {
    return <ClaimPOSTab />;
  }

  const { isMobile } = uiState;

  return (
    <>
      <Switch>
        {/* Removes trailing slash to prevent errors */}
        <Redirect from="/:url*(/+)" to={location.pathname.slice(0, -1) + location.search} />
        <Route exact path="/:locationCode" component={MenuPage} />
        <Route exact path="/:locationCode/(add|edit)" component={MenuPage} />
        {/* If Enable Checkout is off, direct attempts at viewing checkout cart should redirect. */}
        {!locationStore.enable_checkout && <Redirect from="/:locationCode/checkout" to="/:locationCode" />}
        {/* On desktop route directly to checkout page, on mobile still load menu page in background */}
        <Route exact={isMobile} path="/:locationCode/checkout" component={isMobile ? MenuPage : CheckoutRoute} />

        {/* If none of the above routes match then redirect to the home page */}
        <Redirect from="*" to="/" />
      </Switch>

      {kiosk_enabled && stripeTerminalStore.loaded && stripeTerminalStore.showReaderPairingModal && (
        <ReaderPairingModal />
      )}

      {kiosk_enabled && stripeTerminalStore.loaded && stripeTerminalStore.showReaderDisconnectedModal && (
        <ReaderDisconnectedModal />
      )}

      {/* Modals foreground */}
      <Route exact path="/:locationCode/add" component={CartItemAddModalContainer} />
      <Route exact path="/:locationCode/edit" component={CartItemEditModalContainer} />
    </>
  );
}

export default observer(LocationRoute);
